import React, { useEffect, useState } from "react";
import { 
  Phone, 
  Mail, 
  Navigation, 
  MessageCircle, 
  AlertTriangle,
  Building2,
  MapPin,
  ChevronRight,
  Headset
} from "lucide-react";
import Activity from "../components/Dashboardloader.jsx"; 
import api from "../api/Api"; 
import { toast } from "sonner"; 

const ContactOfficePage = () => {
  const [loading, setLoading] = useState(true);
  const [contactData, setContactData] = useState(null);

  // --- FETCH DATA FROM API ---
  useEffect(() => {
    const fetchContactDetails = async () => {
      try {
        const res = await api.get("/contact/contact-details");
        if (res.data.success) {
          setContactData(res.data.data);
        }
      } catch (err) {
        console.error("Failed to load contact info", err);
        toast.error("Could not load contact details");
      } finally {
        setLoading(false);
      }
    };

    fetchContactDetails();
  }, []);

  // --- ROBUST LINK OPENER ---
  const openLink = (url) => {
    if (!url) return;

    if (url.startsWith("tel:") || url.startsWith("mailto:")) {
      window.location.href = url;
      return;
    }

    try {
      const newWindow = window.open(url, "_system");
      if (!newWindow) {
        window.location.href = url; 
      }
    } catch (e) {
      window.location.href = url;
    }
  };

  // --- LOADING STATE ---
  if (loading) {
    return (
      <div className="loader-container">
        <Activity />
        <style>{`
            .loader-container {
                position: fixed; top: 0; left: 0; width: 100%; height: 100%; 
                display: flex; flex-direction: column; justify-content: center; 
                align-items: center; background: #f8fafc;
            }
        `}</style>
      </div>
    );
  }

  // --- ERROR STATE ---
  if (!contactData) return (
    <div className="d-flex flex-column align-items-center justify-content-center min-vh-100 bg-light p-5 text-center">
      <div className="bg-white p-4 rounded-circle shadow-sm mb-3">
        <AlertTriangle size={40} className="text-warning opacity-75" />
      </div>
      <h5 className="text-dark fw-bold mb-1">Support Unavailable</h5>
      <p className="text-muted small mb-4">We couldn't reach the support servers.</p>
      <button className="btn btn-primary rounded-pill px-4 fw-bold shadow-sm native-press" onClick={() => window.location.reload()}>
        Try Again
      </button>
    </div>
  );

  return (
    <div className="min-vh-100 bg-surface font-native pb-5 overflow-x-hidden">
      
      {/* === 1. PREMIUM HERO HEADER === */}
      <div className="hero-section position-relative pt-5 pb-5 px-4 text-white text-center">
        {/* Subtle Watermark Icon */}
        <Headset 
          size={200} 
          className="position-absolute text-white" 
          style={{ right: "-40px", top: "-20px", opacity: 0.08, transform: "rotate(-10deg)" }} 
        />
        
        <div className="position-relative z-1 mb-4 pt-2">
          <h2 className="fw-bolder mb-1 text-white tracking-tight" style={{ fontSize: "1.75rem" }}>
            Help & Support
          </h2>
          <p className="text-white text-opacity-75 small fw-medium m-0">
            We are here to help you 24/7
          </p>
        </div>
      </div>

      {/* === 2. MAIN CONTENT AREA === */}
      <div className="px-2 position-relative z-2 mx-auto" style={{ maxWidth: "600px", marginTop: "-60px" }}>
        
        {/* A. PRIMARY CONTACT CARD (Native List Style) */}
        <div className="card premium-card mb-4 animate-slide-up">
          
          {/* WhatsApp Action (Hero Button) */}
          <div className="p-3 pb-2">
            <button 
              onClick={() => openLink(contactData.whatsapp?.link)}
              className="btn w-100 rounded-4 py-3 fw-bold d-flex align-items-center justify-content-center gap-2 native-press whatsapp-btn"
            >
              <MessageCircle size={22} fill="white" />
              {contactData.whatsapp?.label || "Chat with Operations"}
            </button>
          </div>

          <hr className="border-light m-0 mx-3" />

          {/* Call Support (List Item) */}
          <div 
            onClick={() => openLink(`tel:${contactData.primary_support?.phone}`)}
            className="native-list-item px-3 py-3 d-flex align-items-center gap-3 native-press"
          >
            <div className="icon-box bg-primary-subtle text-primary">
              <Phone size={20} />
            </div>
            <div className="flex-grow-1">
              <h6 className="mb-0 fw-bold text-dark fs-6">Call Support</h6>
              <span className="text-muted fw-medium" style={{ fontSize: "0.80rem" }}>
                {contactData.primary_support?.phone}
              </span>
            </div>
            <ChevronRight size={20} className="text-muted opacity-50" />
          </div>

          <hr className="border-light m-0 mx-3" />

          {/* Email Ops (List Item) */}
          <div 
            onClick={() => openLink(`mailto:${contactData.primary_support?.email}`)}
            className="native-list-item px-3 py-3 d-flex align-items-center gap-3 native-press"
          >
            <div className="icon-box bg-danger-subtle text-danger">
              <Mail size={20} />
            </div>
            <div className="flex-grow-1 overflow-hidden">
              <h6 className="mb-0 fw-bold text-dark fs-6">Email Operations</h6>
              <span className="text-muted fw-medium text-truncate d-block" style={{ fontSize: "0.80rem" }}>
                {contactData.primary_support?.email}
              </span>
            </div>
            <ChevronRight size={20} className="text-muted opacity-50" />
          </div>
        </div>

        {/* B. OFFICE HUBS LIST */}
        <div className="d-flex align-items-center justify-content-between mb-3 px-2 mt-4 animate-slide-up" style={{ animationDelay: "0.1s" }}>
          <h6 className="fw-bold text-secondary mb-0 text-uppercase tracking-wide" style={{ fontSize: "0.75rem" }}>
            Visit Our Hubs
          </h6>
          <span className="badge bg-white text-dark shadow-sm rounded-pill px-3 py-2 fw-bold border">
            {contactData.offices?.length || 0} Locations
          </span>
        </div>

        <div className="d-flex flex-column gap-3 animate-slide-up" style={{ animationDelay: "0.2s" }}>
          {contactData.offices?.map((loc) => (
            <div key={loc.id} className="card premium-card overflow-hidden native-press-light">
              <div className="card-body p-0">
                <div className="p-3 pb-2 d-flex gap-3 align-items-start">
                  
                  {/* Location Icon */}
                  <div className={`flex-shrink-0 icon-box-sm bg-${loc.color}-subtle text-${loc.color} mt-1`}>
                    {loc.color === "primary" ? <Building2 size={18} /> : <MapPin size={18} />}
                  </div>
                  
                  {/* Location Details */}
                  <div className="flex-grow-1">
                    <div className="d-flex justify-content-between align-items-center mb-1">
                      <h6 className="fw-bold text-dark mb-0 fs-6">
                        {loc.name}
                      </h6>
                    </div>
                    <span className="badge bg-light text-secondary border mb-2 fw-semibold" style={{ fontSize: "0.65rem" }}>
                      {loc.type}
                    </span>
                    <p className="text-muted fw-medium mb-2 pe-2" style={{ fontSize: "0.85rem", lineHeight: "1.5" }}>
                      {loc.address}
                    </p>
                  </div>
                </div>
                
                {/* Action Strip */}
                <div className="px-3 pb-3 pt-1">
                  <button 
                    onClick={() => openLink(loc.map_url)}
                    className="btn btn-light w-100 rounded-3 d-flex align-items-center justify-content-center gap-2 py-2 fw-bold text-primary native-press border-0"
                    style={{ backgroundColor: "#f0f7ff" }}
                  >
                    <Navigation size={16} strokeWidth={2.5} /> Get Directions
                  </button>
                </div>

              </div>
            </div>
          ))}
        </div>

      </div>

      {/* === PREMIUM CSS STYLES === */}
      <style>{`
        /* Global Native Font & Background */
        .font-native { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif; }
        .bg-surface { background-color: #ffffff; }
        .tracking-tight { letter-spacing: -0.5px; }
        .tracking-wide { letter-spacing: 0.5px; }
        
        /* Premium Hero Gradient */
        .hero-section {
          background: linear-gradient(135deg, #0A66C2 0%, #004182 100%);
          box-shadow: 0 4px 20px rgba(10, 102, 194, 0.2);
        }

        /* High-End Card Design */
        .premium-card {
          border: 1px solid rgba(0,0,0,0.03) !important;
          box-shadow: 0 10px 30px rgba(0,0,0,0.04) !important;
          border-radius: 24px !important;
          background: #ffffff;
          overflow: hidden;
        }

        /* Native List Item (Replaces the awkward side-by-side squares) */
        .native-list-item {
          transition: background-color 0.2s ease;
          cursor: pointer;
        }
        .native-list-item:active { background-color: #f8f9fa; }

        /* Icon Containers */
        .icon-box {
          width: 48px; height: 48px;
          border-radius: 14px;
          display: flex; align-items: center; justify-content: center;
        }
        .icon-box-sm {
          width: 40px; height: 40px;
          border-radius: 12px;
          display: flex; align-items: center; justify-content: center;
        }

        /* WhatsApp Button */
        .whatsapp-btn {
          background: #25D366;
          color: white;
          border: none;
          box-shadow: 0 6px 16px rgba(37, 211, 102, 0.25);
          transition: transform 0.15s ease, box-shadow 0.15s ease;
        }
        .whatsapp-btn:active {
          background: #20BD5A;
          box-shadow: 0 2px 8px rgba(37, 211, 102, 0.2);
        }

        /* Android Native Press Effects */
        .native-press { transition: transform 0.1s ease-out; cursor: pointer; }
        .native-press:active { transform: scale(0.96) !important; }
        
        .native-press-light { transition: transform 0.15s ease-out; cursor: pointer; }
        .native-press-light:active { transform: scale(0.98) !important; }

        /* Bootstrap Color Fallbacks */
        .bg-primary-subtle { background-color: #e0f0ff !important; }
        .text-primary { color: #0A66C2 !important; }
        
        .bg-danger-subtle { background-color: #ffe5e5 !important; }
        .text-danger { color: #d32f2f !important; }

        .bg-info-subtle { background-color: #e0f7fa !important; }
        .bg-dark-subtle { background-color: #e9ecef !important; }

        /* Smooth Slide Up Animation */
        .animate-slide-up { 
           opacity: 0;
           animation: slideUpFade 0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards; 
        }

        @keyframes slideUpFade { 
          from { opacity: 0; transform: translateY(20px); } 
          to { opacity: 1; transform: translateY(0); } 
        }
      `}</style>
    </div>
  );
};

export default ContactOfficePage;